import React from "react";
import { useNavigate } from "react-router-dom";
import config from "../configs/config";
import affordaLogo from "../assets/afforda-logo.svg";
import footerImage from "../assets/footer-image.svg";

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const handleInternalLink = (
    e: React.MouseEvent<HTMLAnchorElement>,
    path: string,
  ) => {
    e.preventDefault();
    navigate(path);
  };

  return (
    <footer className="bg-grey-20 py-8">
      <div className="container-footer">
        <div className="bg-primary rounded-20 p-[20px] lg:p-[40px]">
          {/* Top Row: Logo (50%) | Sitemap & Social (50%) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Logo Section */}
            <div className="flex justify-center md:justify-start">
              <img src={affordaLogo} alt="Afforda" className="h-12 w-auto" />
            </div>

            {/* Navigation Section */}
            <div className="space-y-6">
              {/* Legal Section */}
              <div>
                <h3 className="text-base font-bold text-black mb-2 tracking-wider text-xl">
                  LEGAL
                </h3>
                <div className="text-2xl text-black flex flex-wrap break-words">
                  <a
                    href={`${config.AFFORDA_STATIC_SITE}/privacy-policy/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mr-1"
                  >
                    Privacy policy,
                  </a>
                  <a
                    href={`${config.AFFORDA_STATIC_SITE}/cookie-policy/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mr-1"
                  >
                    Cookie policy,
                  </a>
                  <a
                    href={`${config.AFFORDA_STATIC_SITE}/terms-of-use/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mr-1"
                  >
                    Terms of use,
                  </a>
                  <a
                    href="/preferences"
                    onClick={(e) => handleInternalLink(e, "/preferences")}
                  >
                    Marketing consent
                  </a>
                </div>
              </div>

              {/* Help Section */}
              <div>
                <h3 className="text-base font-bold text-black mb-2 tracking-wider text-xl">
                  HELP
                </h3>
                <div className="text-2xl text-black flex flex-wrap break-words">
                  <a
                    href={`${config.AFFORDA_STATIC_SITE}/faqs/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mr-1"
                  >
                    FAQs,
                  </a>
                  <a
                    href={`${config.AFFORDA_STATIC_SITE}/resources/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mr-1"
                  >
                    Resources,
                  </a>
                  <a
                    href="/contact-us"
                    onClick={(e) => handleInternalLink(e, "/contact-us")}
                    className="mr-1"
                  >
                    Contact us,
                  </a>
                  <a
                    href="/contact-us?type=complaint"
                    onClick={(e) =>
                      handleInternalLink(e, "/contact-us?type=complaint")
                    }
                    className="mr-1"
                  >
                    Make a complaint,
                  </a>
                  <a
                    href={`${config.AFFORDA_STATIC_SITE}/sitemap/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Sitemap
                  </a>
                </div>
              </div>

              {/* Social Section */}
              <div>
                <h3 className="text-base font-bold text-black mb-2 tracking-wider text-xl">
                  SOCIAL
                </h3>
                <div className="text-2xl text-black flex flex-wrap break-words">
                  <a
                    href="https://twitter.com/afforda_uk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mr-1"
                  >
                    X,
                  </a>
                  <a
                    href="https://www.youtube.com/@Afforda"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mr-1"
                  >
                    Youtube,
                  </a>
                  <a
                    href="https://www.facebook.com/afforda.UK"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mr-1"
                  >
                    Facebook,
                  </a>
                  <a
                    href="https://www.tiktok.com/@afforda.uk"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    TikTok
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Row: Footer Image (50%) | Legal Text & Copyright (50%) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            {/* Footer Image */}
            <div className="flex justify-center md:justify-start">
              <img src={footerImage} alt="Afforda Character" />
            </div>

            {/* Legal Text & Copyright */}
            <div className="text-sm text-black space-y-4">
              <div>
                <p className="font-bold text-black mb-2">
                  Important information
                </p>
                <p className="mb-4">
                  Afforda is a trading name of GAIN Credit LLC. Afforda is
                  acting as a credit broker and not a lender. Registered
                  Address: 251 Little Falls Drive, Wilmington, DE 19808, USA.
                  Registration Number: 4124111. Authorised and Regulated by the
                  Financial Conduct Authority. Registration Number 689378.
                  Licensed by the Information Commissioners Office,
                  (registration number Z2752028). Contact Address: Afforda, PO
                  Box 10756, Leicester, LE3 4GX.
                </p>

                <p className="mb-4">
                  We are a credit broker and may pay commission to credit
                  brokers or other intermediaries who introduce customers to us.
                  We may also receive commission from credit brokers or
                  intermediaries who we have introduced customers to. Any
                  commission received or paid can vary depending on the broker,
                  the product offered, and the terms agreed with the broker.
                  This commission is not charged to you and does not affect the
                  total cost of your credit product.
                </p>
              </div>

              <div className="pt-4 border-t border-black">
                <span className="text-black">
                  © {currentYear} Afforda. All Rights Reserved.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
